import React, { useState, useEffect, useCallback } from 'react';
import { Baby, Briefcase, Users, Scissors, Plus, Trash2, Check } from 'lucide-react';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { useSalvaCampo, conInvio } from '../../utils/salvaCampo';
import StatoSalvataggio from '../StatoSalvataggio';
import { anamnesiFisiologicaAPI } from '../../services/api';
import RiquadroGravidanze, { CAMPI_DEL_RIQUADRO } from './RiquadroGravidanze';
import '../../styles/SchedaFisiologica.css';

/**
 * SchedaFisiologica — parto, lavoro, famiglia, interventi chirurgici.
 *
 * PERCHE' NON SONO "VOCI DI ANAMNESI"
 * Una voce di anamnesi ha un autore, una gravita', e si dichiara
 * superata quando non vale piu'. Il tipo di parto non scade e non ha
 * gravita': e' un fatto. Percio' sta in un riquadro a campi, salvato in
 * blocco, e non nell'elenco delle voci.
 *
 * I QUATTRO RIQUADRI NON STANNO INSIEME
 * Vanno intercalati fra le voci, ognuno accanto alla sezione con cui si
 * legge: il parto dopo le terapie in corso, gli interventi chirurgici
 * dopo le patologie pregresse, la famiglia dopo l'anamnesi familiare, il
 * lavoro dopo la famiglia. Chi legge un'anamnesi non salta avanti e
 * indietro fra due blocchi.
 *
 * Percio' questo componente non disegna una schermata: tiene lo stato -
 * una riga per paziente, un salvataggio solo - e consegna i quattro
 * riquadri a chi lo contiene, che li mette dove vanno. Si usa cosi':
 *
 *   <SchedaFisiologica pazienteId={id}>
 *     {(r) => <>… {r.parto} … {r.interventi} … {r.azioni}</>}
 *   </SchedaFisiologica>
 *
 * L'alternativa era portare lo stato in AnamnesiTab e spargere la logica
 * del salvataggio in mezzo al codice delle voci.
 *
 * I CAMPI ESISTEVANO GIA'
 * `parto`, `allattamento` e `lavoro` stavano in
 * `anamnesi_fisiologica_dettaglio` con la rotta che li legge e li
 * scrive da mesi: mancava soltanto la schermata. Note, conviventi e
 * interventi sono stati aggiunti il 4 settembre 2026.
 *
 * COME SI SALVA (regola di Alex del 24 settembre 2026, e 14 ottobre 2026)
 * Una scrittura per campo, quando si ESCE dal campo - non a ogni tasto, che
 * riempirebbe l'audit di righe che non raccontano niente. Gli elenchi
 * (conviventi, interventi) sono una colonna sola e si scrivono interi.
 * Ogni riquadro DICE com'e' andata (StatoSalvataggio: «Salvato ✓ 14:32»,
 * «Non salvato», «Sto salvando…») e la riga si salva anche con Invio (senza
 * spostare il fuoco), togliendola, e se il riquadro si smonta senza che il
 * campo abbia perso il fuoco (cambio di linguetta): vedi utils/salvaCampo.js.
 * Il «Riprova a salvare» in fondo riprova i campi rossi, uno per uno.
 */

const PARTO = [
    { valore: 'naturale', etichetta: 'Parto naturale' },
    { valore: 'cesareo', etichetta: 'Parto cesareo' },
];

const ALLATTAMENTO = [
    { valore: 'seno', etichetta: 'Allattamento al seno' },
    { valore: 'artificiale', etichetta: 'Allattamento artificiale' },
];

/* GRAVIDANZE, CLIMATERIO E MENOPAUSA non stanno piu' qui: sono
   RiquadroGravidanze, che carica e salva da se' (23 settembre 2026, un solo
   dato scrivibile da due posti: l'anamnesi e la scheda ginecologica). Questo
   componente gli da' solo il posto, sotto «Parto». I suoi campi si tolgono
   da `dati` al caricamento: il salvataggio in blocco di questa scheda non
   deve riscriverli con i valori letti all'apertura - sarebbe un dato vecchio
   sopra quello che la ginecologa ha appena scritto dalla scheda. */

/* Cinque posti, come chiesto. Il suggerimento sotto vale per tutti e
   cinque: intestare ogni casella con «marito/moglie», «figlio 1»…
   costringerebbe chi ha due figli e un genitore a incastrare i propri
   familiari in etichette sbagliate. */
const POSTI_CONVIVENTI = 5;

const messaggioErrore = (e, ripiego) =>
    e?.message || e?.response?.data?.message || ripiego;

/* Citato alla lettera dal manuale dei medici. */
const MSG_DATE_INTERVENTI = 'Le date degli interventi vanno scritte come 2019, 2019-03 oppure 2019-03-14.';

const vuota = () => ({ data: '', descrizione: '' });

/* Cio' che si scrive di un elenco: le righe che hanno qualcosa. Il server
   toglie comunque le righe vuote; farlo qui fa si' che aggiungere una riga e
   lasciarla bianca non conti come una modifica, e che il confronto con
   l'archivio dica la verita'. */
const interventiScritti = (lista) => lista
    .map((x) => ({ data: String(x.data ?? '').trim(), descrizione: String(x.descrizione ?? '').trim() }))
    .filter((x) => x.data || x.descrizione);
const conviventiScritti = (lista) => lista.map((x) => String(x ?? '').trim()).filter(Boolean);

/* I campi di testo del Parto, del Lavoro e della Famiglia, e quelli che sono
   di ciascun riquadro: lo stato di un riquadro si calcola dai suoi. */
const CAMPI_TESTO = ['parto_note', 'allattamento_note', 'lavoro', 'famiglia_note'];
const CAMPI_PARTO = ['parto', 'parto_note', 'allattamento', 'allattamento_note'];
const CAMPI_LAVORO = ['lavoro'];
const CAMPI_FAMIGLIA = ['conviventi', 'famiglia_note'];
const CAMPI_INTERVENTI = ['interventi'];

/* Le tre forme ammesse per la data di un intervento: solo anno, mese e
   anno, oppure la data intera. Chi ricorda solo l'anno non deve
   inventare un giorno. */
const DATA_PARZIALE = /^\d{4}(-\d{2}(-\d{2})?)?$/;

const SchedaFisiologica = ({ pazienteId, children }) => {
    const [dati, setDati] = useState(null);
    const [conviventi, setConviventi] = useState(Array(POSTI_CONVIVENTI).fill(''));
    const [interventi, setInterventi] = useState([vuota()]);
    const [caricamento, setCaricamento] = useState(true);
    /* «Modificato» e' un confronto con l'istantanea presa al
       caricamento e a ogni salvataggio (`versione` sale li'), non un
       flag alzato a ogni tasto: vedi utils/uscitaProtetta.js. */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco({ dati, conviventi, interventi }, versione);
    const [errore, setErrore] = useState(null);

    /* IL SALVATAGGIO AUTOMATICO, un campo per volta, quando il medico
       esce dal campo. Il pulsante «Salva» resta: e' il modo di riprovare
       tutto insieme quando piu' campi sono rossi. Regole e perche' in
       utils/salvaCampo.js. */
    const { stato: statoCampi, salva: salvaCampo, nonSalvati, salvatoAlle, segnaBase, uscita } = useSalvaCampo(
        useCallback((campo, valore) => anamnesiFisiologicaAPI.saveDettaglio(
            pazienteId, { [campo]: valore }), [pazienteId]),
        /* Quel pezzo e' in archivio: l'istantanea si rifa', altrimenti la
           barra continuerebbe a dire «modifiche non salvate» per un
           modulo che e' stato salvato campo per campo. Quello che NON e'
           passato resta rosso e si conta da solo. */
        useCallback(() => setVersione((v) => v + 1), []),
        /* Il riquadro si smonta senza che il campo perda il fuoco (cambio di
           linguetta, Ctrl K): quello che e' scritto e non uscito si salva
           comunque. Scrivono solo i campi diversi dall'archivio. */
        { allaChiusura: () => salvaAperti(), bloccato: () => dateSbagliate.length > 0 });

    /* La guardia d'uscita guarda anche i campi ROSSI, non solo il confronto
       con l'istantanea: quando un campo si salva, l'istantanea si rifa' con i
       valori di tutti, compreso quello il cui salvataggio e' fallito, e il
       modulo sembrava pulito - si usciva senza avviso con un campo non in
       cartella (trovato il 22 settembre 2026). Il numero e' lo stesso della
       barra: la domanda dice «2 campi non salvati» come la barra. */
    useUscitaProtetta(sporco || nonSalvati.length > 0, nonSalvati.length || null, uscita);

    /* La spunta e il bordo rosso accanto a un campo. */
    const segno = (campo) => {
        const st = statoCampi[campo];
        if (!st) return null;
        if (st === 'in_corso') return <span className="sf-segno sf-segno--corso" title="Sto salvando…">…</span>;
        if (st === 'salvato') return <span className="sf-segno sf-segno--ok" title="Salvato"><Check size={13} aria-hidden="true" /></span>;
        return (
            <button type="button" className="sf-segno sf-segno--no"
                    title="Non salvato: clicca per riprovare"
                    onClick={() => salvaCampo(campo, valoreDi(campo))}>non salvato</button>
        );
    };
    const classeCampo = (campo) => (statoCampi[campo] === 'non_salvato' ? 'sf-testo--rosso' : '');

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await anamnesiFisiologicaAPI.getDettaglio(pazienteId);
            /* `dettaglio` e' null quando il paziente non ha ancora
               nessuna anamnesi: e' il caso normale la prima volta, non un
               errore. Il salvataggio crea la riga da se'. */
            const d = { ...(r.data?.data?.dettaglio || {}) };
            CAMPI_DEL_RIQUADRO.forEach((c) => { delete d[c]; });
            setDati(d);

            /* Le cinque caselle si riempiono con quello che c'e' e si
               completano con caselle vuote: chi ne aveva scritti due
               deve trovare tre posti liberi, non due caselle e basta. */
            const salvati = Array.isArray(d.conviventi) ? d.conviventi : [];
            setConviventi([
                ...salvati,
                ...Array(Math.max(0, POSTI_CONVIVENTI - salvati.length)).fill(''),
            ]);

            const inter = Array.isArray(d.interventi) ? d.interventi : [];
            const righeInter = inter.length
                ? inter.map((x) => ({ data: x?.data || '', descrizione: x?.descrizione || '' }))
                : [vuota()];
            setInterventi(righeInter);
            /* Cio' che c'e' in archivio: un campo che esce com'e' non si
               scrive e non dice «salvato». */
            CAMPI_TESTO.forEach((c) => segnaBase(c, d[c] ?? null));
            segnaBase('conviventi', conviventiScritti(salvati));
            segnaBase('interventi', interventiScritti(righeInter));
            setVersione((v) => v + 1);
        } catch (e) {
            setErrore(messaggioErrore(e, 'Non riesco a leggere la scheda'));
        } finally {
            setCaricamento(false);
        }
    }, [pazienteId, segnaBase]);

    useEffect(() => { carica(); }, [carica]);

    /* Il valore di adesso, per il ritentativo: gli elenchi non stanno in
       `dati` ma nei loro stati, e si scrivono come li scrive il salvataggio
       (le sole righe che hanno qualcosa). */
    const valoreDi = (campo) => {
        if (campo === 'conviventi') return conviventiScritti(conviventi);
        if (campo === 'interventi') return interventiScritti(interventi);
        return dati?.[campo] || null;
    };

    const cambia = (campo, valore) => {
        setDati((p) => ({ ...(p || {}), [campo]: valore }));
    };

    /* Ripremendo la scelta già scelta la si annulla: senza, chi tocca
       «cesareo» per sbaglio non ha nessun modo di tornare a «non
       indicato», e in cartella resta un dato che nessuno ha dichiarato. */
    const scegli = (campo, valore) => {
        const nuovo = dati?.[campo] === valore ? null : valore;
        cambia(campo, nuovo);
        /* Una spunta non ha un'uscita dal campo: si salva al clic. */
        salvaCampo(campo, nuovo);
    };

    const cambiaConvivente = (i, v) => {
        setConviventi((p) => p.map((x, k) => (k === i ? v : x)));
    };
    const salvaConviventi = () => salvaCampo('conviventi', conviventiScritti(conviventi));

    const cambiaIntervento = (i, campo, v) => {
        setInterventi((p) => p.map((x, k) => (k === i ? { ...x, [campo]: v } : x)));
    };

    const aggiungiIntervento = () => {
        setInterventi((p) => [...p, vuota()]);
    };

    /* Gli interventi sono una colonna sola (jsonb): si salva l'elenco
       intero. Una data scritta male non parte - il server la scarterebbe, e
       in cartella una data sparita in silenzio sembra una data mai data - e
       il perche' si legge sotto l'elenco, con il riquadro che dice «Non
       salvato». */
    const salvaInterventiDi = (lista) => {
        if (lista.some((x) => x.data && !DATA_PARZIALE.test(String(x.data).trim()))) return;
        salvaCampo('interventi', interventiScritti(lista));
    };
    const salvaInterventi = () => salvaInterventiDi(interventi);

    /* Togliere una riga e' una modifica dell'elenco come le altre: senza
       questa scrittura la riga tolta tornava al ricaricamento. */
    const togliIntervento = (i) => {
        const lista = interventi.length === 1 ? [vuota()] : interventi.filter((_, k) => k !== i);
        setInterventi(lista);
        salvaInterventiDi(lista);
    };

    const dateSbagliate = interventi
        .map((x, i) => ({ i, ok: !x.data || DATA_PARZIALE.test(x.data.trim()) }))
        .filter((x) => !x.ok)
        .map((x) => x.i);

    /* Tutto cio' che e' scritto e puo' non essere ancora uscito: per lo
       smontaggio. Scrive solo cio' che differisce dall'archivio. */
    const salvaAperti = () => {
        CAMPI_TESTO.forEach((c) => salvaCampo(c, dati?.[c] || null));
        salvaConviventi();
        salvaInterventi();
        return dateSbagliate.length === 0;
    };

    /* «Riprova a salvare»: riprova, uno per uno, i campi rossi. Prima
       riscriveva l'intera scheda in blocco: poteva riportare in archivio un
       valore letto all'apertura sopra uno scritto nel frattempo, e i segni
       rossi non si toglievano nemmeno a salvataggio riuscito. */
    const riprova = (campi = null) => {
        setErrore(null);
        const ancora = nonSalvati.filter((c) => (!campi || campi.includes(c)) && c !== 'interventi');
        ancora.forEach((c) => salvaCampo(c, valoreDi(c)));
        /* Gli interventi si riprovano anche se il riquadro e' «bloccato» da
           una data scritta male: la funzione rifa' il controllo. */
        if (!campi ? nonSalvati.includes('interventi') : campi.includes('interventi')) salvaInterventi();
    };

    /* Lo stato di UN riquadro, calcolato dai suoi campi. */
    const statoDi = (campi, bloccato = false) => (
        <StatoSalvataggio campi={campi} stato={statoCampi} salvatoAlle={salvatoAlle}
            bloccato={bloccato} onRiprova={() => riprova(campi)} />
    );

    /* Solo al primo caricamento: dopo un salvataggio in blocco si rilegge
       senza smontare i riquadri. Smontarli farebbe perdere a quello delle
       gravidanze - che ha stato suo - i segni «non salvato» dei suoi campi. */
    if (caricamento && dati === null) return null;   // le voci si vedono comunque

    /* ---------- Parto e allattamento ---------- */
    const bloccoParto = (
            <section data-salva-da-solo className="sf-riquadro">
                <h3><Baby size={16} strokeWidth={1.8} aria-hidden="true" /> Parto</h3>
                {statoDi(CAMPI_PARTO)}

                <div className="sf-riga-scelta">
                    <div className="sf-spunte">
                        {PARTO.map((o) => (
                            <label key={o.valore} className="sf-spunta">
                                <input type="checkbox"
                                    checked={dati?.parto === o.valore}
                                    onChange={() => scegli('parto', o.valore)} />
                                <span>{o.etichetta}</span>
                            </label>
                        ))}
                    </div>
                    <input className={`sf-testo ${classeCampo('parto_note')}`}
                        placeholder="Note sul parto"
                        value={dati?.parto_note || ''}
                        onChange={(e) => cambia('parto_note', e.target.value)}
                        onBlur={(e) => salvaCampo('parto_note', e.target.value || null)}
                        onKeyDown={conInvio((e) => salvaCampo('parto_note', e.target.value || null))} />
                    {segno('parto_note')}
                </div>

                <div className="sf-riga-scelta">
                    <div className="sf-spunte">
                        {ALLATTAMENTO.map((o) => (
                            <label key={o.valore} className="sf-spunta">
                                <input type="checkbox"
                                    checked={dati?.allattamento === o.valore}
                                    onChange={() => scegli('allattamento', o.valore)} />
                                <span>{o.etichetta}</span>
                            </label>
                        ))}
                    </div>
                    <input className={`sf-testo ${classeCampo('allattamento_note')}`}
                        placeholder="Note sull'allattamento"
                        value={dati?.allattamento_note || ''}
                        onChange={(e) => cambia('allattamento_note', e.target.value)}
                        onBlur={(e) => salvaCampo('allattamento_note', e.target.value || null)}
                        onKeyDown={conInvio((e) => salvaCampo('allattamento_note', e.target.value || null))} />
                    {segno('allattamento_note')}
                </div>
            </section>
    );

    /* ---------- Lavoro ---------- */
    const bloccoLavoro = (
            <section data-salva-da-solo className="sf-riquadro">
                <h3><Briefcase size={16} strokeWidth={1.8} aria-hidden="true" /> Lavoro</h3>
                {statoDi(CAMPI_LAVORO)}
                <input className={`sf-testo sf-testo--pieno ${classeCampo('lavoro')}`}
                    placeholder="Attività lavorativa, turni, esposizioni"
                    value={dati?.lavoro || ''}
                    onChange={(e) => cambia('lavoro', e.target.value)}
                    onBlur={(e) => salvaCampo('lavoro', e.target.value || null)}
                    onKeyDown={conInvio((e) => salvaCampo('lavoro', e.target.value || null))} />
                {segno('lavoro')}
            </section>
    );

    /* ---------- Famiglia ---------- */
    const bloccoFamiglia = (
            <section data-salva-da-solo className="sf-riquadro">
                <h3><Users size={16} strokeWidth={1.8} aria-hidden="true" /> Famiglia</h3>
                {statoDi(CAMPI_FAMIGLIA)}
                <span className="sf-etichetta">Conviventi</span>
                <div className="sf-conviventi">
                    {conviventi.map((v, i) => (
                        <input key={i} className="sf-testo"
                            value={v}
                            onChange={(e) => cambiaConvivente(i, e.target.value)}
                            onBlur={salvaConviventi}
                            onKeyDown={conInvio(salvaConviventi)} />
                    ))}
                </div>
                <span className="sf-aiuto">marito / moglie, figli, altri</span>
                <input className={`sf-testo sf-testo--pieno ${classeCampo('famiglia_note')}`}
                    placeholder="Altro sulla situazione familiare"
                    value={dati?.famiglia_note || ''}
                    onChange={(e) => cambia('famiglia_note', e.target.value)}
                    onBlur={(e) => salvaCampo('famiglia_note', e.target.value || null)}
                    onKeyDown={conInvio((e) => salvaCampo('famiglia_note', e.target.value || null))} />
                {segno('famiglia_note')}
            </section>
    );

    /* ---------- Interventi chirurgici ---------- */
    const bloccoInterventi = (
            <section data-salva-da-solo className="sf-riquadro">
                <h3><Scissors size={16} strokeWidth={1.8} aria-hidden="true" /> Interventi chirurgici</h3>
                {statoDi(CAMPI_INTERVENTI, dateSbagliate.length > 0)}
                <span className="sf-aiuto">
                    La data può essere incompleta: <b>2019</b>, <b>2019-03</b> oppure
                    <b> 2019-03-14</b>. Chi ricorda solo l&apos;anno non deve inventare un giorno.
                </span>

                <div className="sf-interventi">
                    {interventi.map((x, i) => (
                        <div key={i} className="sf-intervento">
                            <input
                                className={`sf-testo sf-data ${dateSbagliate.includes(i) ? 'errore' : ''}`}
                                placeholder="2019-03"
                                value={x.data}
                                onChange={(e) => cambiaIntervento(i, 'data', e.target.value)}
                                onBlur={salvaInterventi}
                                onKeyDown={conInvio(salvaInterventi)} />
                            <input className="sf-testo"
                                placeholder="Intervento e note"
                                value={x.descrizione}
                                onChange={(e) => cambiaIntervento(i, 'descrizione', e.target.value)}
                                onBlur={salvaInterventi}
                                onKeyDown={conInvio(salvaInterventi)} />
                            <button type="button" className="sf-toglie"
                                onClick={() => togliIntervento(i)}
                                title="Togli questa riga">
                                <Trash2 size={15} aria-hidden="true" />
                            </button>
                        </div>
                    ))}
                </div>

                {dateSbagliate.length > 0 && (
                    <span className="sf-errore-riga">
                        {MSG_DATE_INTERVENTI} Finché non è corretta, l&apos;elenco non si salva.
                    </span>
                )}

                <button type="button" className="sf-btn" onClick={aggiungiIntervento}>
                    <Plus size={15} aria-hidden="true" /> Aggiungi un intervento
                </button>
            </section>
    );

    /* ---------- In fondo: solo cio' che serve quando qualcosa NON e' passato ----------
       NIENTE «SALVA» (regola di Alex del 24/09/2026): ogni campo si salva da
       solo quando se ne esce, e ogni riquadro dice com'e' andata. In fondo
       restano il conto dei campi rossi e «Riprova a salvare», che li rimanda
       tutti insieme: non sparisce da solo, perche' finche' e' li' c'e' del
       testo che non e' in cartella. */
    const bloccoAzioni = (nonSalvati.length || errore) ? (
        <div className={`sf-azioni ${nonSalvati.length ? 'da-salvare' : ''}`}>
            {errore && <span className="sf-errore-riga">{errore}</span>}
            {nonSalvati.length > 0 && (
                <span className="sf-sporco">
                    {nonSalvati.length === 1 ? 'Un campo non salvato' : `${nonSalvati.length} campi non salvati`}
                </span>
            )}
            {nonSalvati.length > 0 && (
                <button type="button" className="sf-btn sf-btn--principale" onClick={() => riprova()}>
                    Riprova a salvare
                </button>
            )}
        </div>
    ) : null;

    /* I nomi delle chiavi sono quelli che usa chi ci contiene; le
       costanti si chiamano `blocco...` perche' `interventi` e' gia' lo
       stato dell'elenco, qualche riga sopra. */
    return children({
        parto: bloccoParto,
        gravidanze: <RiquadroGravidanze pazienteId={pazienteId} origine="anamnesi" />,
        lavoro: bloccoLavoro,
        famiglia: bloccoFamiglia,
        interventi: bloccoInterventi,
        azioni: bloccoAzioni,
    });
};

export default SchedaFisiologica;
